import {createEntityAdapter, EntityAdapter} from '@ngrx/entity';
import {createReducer, on} from '@ngrx/store';
import * as TenantsActions from './tenants.actions';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {Tenant, TenantSearchParams} from '@association/_common/models/tenant.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const tenantsFeatureKey = 'tenants';

export function sortEntities(a: Tenant, b: Tenant) {
    if (a.userName.fullName! !== b.userName.fullName!) {
        return a.userName.fullName!.localeCompare(b.userName.fullName!);
    }

    if (a.id && b.id) {
        return a.id.localeCompare(b.id);
    }

    return 0;
}

export const adapter: EntityAdapter<Tenant> = createEntityAdapter<Tenant>({
    sortComparer: sortEntities
});

export type TenantsState = EntityStateModel<Tenant, TenantSearchParams>;

export const initialState: TenantsState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const tenantsReducer = createReducer(
    initialState,

    on(TenantsActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(TenantsActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(TenantsActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined

            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(TenantsActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(TenantsActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(TenantsActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(TenantsActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(TenantsActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(TenantsActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(TenantsActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(TenantsActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(TenantsActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(TenantsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: TenantSearchParams | undefined, entity: Tenant): boolean => {
    if (!searchParams) {
        return true;
    }

    if (searchParams.query && (
        entity.userName.lastName && entity.userName.lastName?.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1 &&
        entity.userName.firstName && entity.userName.firstName?.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1 &&
        entity.userName.fullName && entity.userName.fullName?.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1 &&
        entity.phones.every(el => el.number.indexOf((searchParams.query as string).toLowerCase()) == -1))) {
        return false;
    }

    if (searchParams.building?.id && entity.apartments.every(el => el.apartment?.building?.id != searchParams.building?.id)) {
        return false;
    }

    if (searchParams.section?.id && entity.apartments.every(el => el.apartment?.section?.id != searchParams.section?.id)) {
        return false;
    }

    if ((searchParams.floor || searchParams.floor === 0) && entity.apartments.every(el => el.apartment?.floor !== searchParams.floor)) {
        return false;
    }

    if (searchParams.apartmentTypes?.length &&
        entity.apartments.every(
            el =>
                !searchParams.apartmentTypes?.some(
                    type => type.id === el.apartment?.type?.id
                )
        )
    ) {
        return false;
    }

    if (searchParams.role && entity.apartments.every(el => el.role != searchParams.role)) {
        return false;
    }

    return !(searchParams.status && entity.user?.userStatus != searchParams.status);
};
